import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { DemoQuote } from "@/components/DemoQuote";
import { useI18n } from "@/context/I18nContext";
import { setSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";

const COPY = {
  it: {
    title: "Generatore di preventivi gratis online (con AI) | Operativo",
    meta: "Crea un preventivo professionale gratis: descrivi il lavoro e l'AI compila voci, quantità, prezzi e IVA. PDF pronto da inviare al cliente. Senza registrazione.",
    h1: "Generatore di preventivi gratis",
    lead: "Descrivi il lavoro con le tue parole e ottieni un preventivo completo: voci di lavorazione, quantità, prezzi, IVA e totale. Senza registrarti, senza carta di credito, senza installare niente.",
    bullets: [
      "Voci di lavorazione scritte dall'AI, non da compilare a mano",
      "Prezzi realistici di mercato, da correggere come vuoi",
      "IVA e totali calcolati da soli",
      "PDF con il tuo logo e i tuoi dati fiscali (nell'app)",
    ],
    forWho: "Per chi è utile",
    forWhoText: "Artigiani, impiantisti, elettricisti, idraulici, imprese edili, fotografi, consulenti, agenzie e liberi professionisti: chiunque debba mandare un preventivo a un cliente e non voglia perderci la serata.",
    howTitle: "Come si fa un preventivo in 30 secondi",
    steps: [
      { t: "1. Racconta il lavoro", d: "Una riga basta: \"ristrutturazione bagno 6 mq, 10 giorni, cliente Rossi\". Puoi aggiungere materiali, tempi, cliente e sconti." },
      { t: "2. Leggi il preventivo", d: "L'AI compila le voci di lavorazione con quantità e prezzi, calcola IVA e totale e propone una nota per il cliente." },
      { t: "3. Correggi e invia", d: "Cambi quello che vuoi, scarichi il PDF e lo mandi. Nell'app il PDF esce con il tuo logo, la tua partita IVA e il tuo IBAN." },
    ],
    faqTitle: "Domande frequenti",
    faq: [
      { q: "È davvero gratis?", a: "Sì. Questa pagina genera preventivi gratis senza registrazione, con un limite di prove al giorno. Creando un account hai 7 giorni con tutto incluso e poi la beta gratuita: preventivi, fatture e PDF illimitati." },
      { q: "I prezzi che propone sono affidabili?", a: "Sono stime di mercato realistiche, pensate per essere corrette da te. Nell'app, invece, il sistema legge i preventivi che hai già fatto e riusa i tuoi importi e le tue diciture." },
      { q: "Posso trasformare il preventivo in fattura?", a: "Sì, nell'app: un clic trasforma il preventivo accettato in fattura, con numerazione progressiva e PDF pronto. Non devi riscrivere niente." },
      { q: "Serve la partita IVA per provarlo?", a: "No. Per provare il generatore non serve nulla. I dati fiscali servono solo se vuoi che compaiano sul PDF che mandi al cliente." },
      { q: "I miei dati restano privati?", a: "Sì. Ogni account è uno spazio separato, i tuoi documenti non vengono usati per addestrare modelli e puoi esportare tutto o cancellare l'account quando vuoi." },
    ],
    ctaTitle: "Preventivi, fatture e solleciti in un unico posto",
    ctaText: "Nell'app il preventivo diventa fattura con un clic, il PDF esce col tuo logo e chi non paga riceve il sollecito scritto dall'AI.",
    ctaBtn: "Inizia la prova gratuita di 7 giorni",
    ctaNote: "Nessuna carta richiesta",
    back: "Torna alla home",
    other: "Versione inglese: free quote generator",
    otherHref: "/free-quote-generator",
  },
  en: {
    title: "Free online quote generator (AI powered) | Operativo",
    meta: "Create a professional quote for free: describe the job and the AI fills in line items, quantities, prices and tax. PDF ready to send. No signup required.",
    h1: "Free quote generator",
    lead: "Describe the job in your own words and get a complete quote: line items, quantities, prices, tax and total. No signup, no credit card, nothing to install.",
    bullets: [
      "Line items written by the AI, not typed by you",
      "Realistic market prices you can adjust",
      "Tax and totals calculated automatically",
      "PDF with your logo and company details (in the app)",
    ],
    forWho: "Who it's for",
    forWhoText: "Contractors, electricians, plumbers, builders, photographers, consultants, agencies and freelancers: anyone who has to send a client a quote and doesn't want to lose the evening over it.",
    howTitle: "How to write a quote in 30 seconds",
    steps: [
      { t: "1. Describe the job", d: "One line is enough: \"bathroom refit 6 sqm, 10 days, client Rossi\". You can add materials, timings, client and discounts." },
      { t: "2. Read the quote", d: "The AI fills in line items with quantities and prices, calculates tax and total, and drafts a note for the client." },
      { t: "3. Adjust and send", d: "Change whatever you want, download the PDF and send it. In the app the PDF carries your logo, tax ID and bank details." },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Is it really free?", a: "Yes. This page generates quotes for free with no signup, with a daily limit. Create an account and you get 7 days with everything unlocked, then the free beta: unlimited quotes, invoices and PDFs." },
      { q: "Are the suggested prices reliable?", a: "They are realistic market estimates, meant to be adjusted by you. Inside the app the system reads the quotes you already sent and reuses your own amounts and wording." },
      { q: "Can I turn the quote into an invoice?", a: "Yes, in the app: one click turns an accepted quote into an invoice, with sequential numbering and a ready PDF. Nothing to retype." },
      { q: "Do I need a registered business to try it?", a: "No. Trying the generator requires nothing. Company details are only needed if you want them printed on the PDF you send." },
      { q: "Is my data private?", a: "Yes. Every account is a separate workspace, your documents are never used to train models, and you can export everything or delete your account anytime." },
    ],
    ctaTitle: "Quotes, invoices and payment chasing in one place",
    ctaText: "In the app a quote becomes an invoice in one click, the PDF carries your logo, and whoever hasn't paid gets a reminder written by the AI.",
    ctaBtn: "Start the 7-day free trial",
    ctaNote: "No card required",
    back: "Back to home",
    other: "Versione italiana: generatore di preventivi gratis",
    otherHref: "/generatore-preventivi-gratis",
  },
};

const PATHS = { it: "/generatore-preventivi-gratis", en: "/free-quote-generator" };

// Pagina pubblica pensata per la ricerca su Google: strumento gratuito + contenuto.
const QuoteGenerator = ({ pageLang = "it" }) => {
  const { lang, setLang } = useI18n();
  const c = COPY[pageLang];

  useEffect(() => {
    if (lang !== pageLang) setLang(pageLang);
  }, [lang, pageLang, setLang]);

  useEffect(
    () =>
      setSeo({
        title: c.title,
        description: c.meta,
        path: PATHS[pageLang],
        faq: c.faq,
        lang: pageLang,
        alternates: PATHS,
      }),
    [c, pageLang]
  );

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="quote-generator-page">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-5 lg:px-16">
        <Link to="/" className="flex items-center gap-2 font-extrabold tracking-tight">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm text-white">
            O
          </span>
          Operativo
        </Link>
        <Link to="/registrati" data-testid="tool-header-cta">
          <Button className="bg-slate-900 text-white hover:bg-slate-800">{c.ctaBtn}</Button>
        </Link>
      </header>

      <section className="px-6 py-16 lg:px-16">
        <div className="max-w-3xl">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">{c.h1}</h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">{c.lead}</p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {c.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <DemoQuote />

      <section className="px-6 py-20 lg:px-16">
        <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{c.howTitle}</h2>
        <div className="mt-10 grid gap-10 md:grid-cols-3">
          {c.steps.map((s) => (
            <div key={s.t}>
              <h3 className="text-base font-semibold md:text-lg">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 max-w-3xl">
          <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{c.forWho}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{c.forWhoText}</p>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50 px-6 py-20 lg:px-16">
        <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{c.faqTitle}</h2>
        <div className="mt-10 grid max-w-4xl gap-8 md:grid-cols-2">
          {c.faq.map(({ q, a }) => (
            <div key={q}>
              <h3 className="text-base font-semibold">{q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950 px-6 py-20 text-white lg:px-16">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{c.ctaTitle}</h2>
          <p className="mt-4 text-sm text-slate-400 md:text-base">{c.ctaText}</p>
          <Link to="/registrati" data-testid="tool-footer-cta">
            <Button className="mt-8 gap-2 bg-white px-6 py-6 text-base text-slate-900 hover:bg-slate-200">
              {c.ctaBtn} <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-slate-500">{c.ctaNote}</p>
        </div>
      </section>

      <footer className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-10 text-sm text-slate-500 lg:px-16">
        <Link to="/" className="hover:text-slate-900">
          {c.back}
        </Link>
        <a href={c.otherHref} className="hover:text-slate-900">
          {c.other}
        </a>
        <Link to="/fattura-elettronica-gratis" className="hover:text-slate-900">
          Fattura elettronica gratis
        </Link>
        <Link to="/privacy" className="hover:text-slate-900">
          Privacy
        </Link>
      </footer>
    </div>
  );
};

export default QuoteGenerator;
